import React, {useState, useEffect } from "react";
import { compose, withStateHandlers } from "recompose";
import { Row, Col, Card, CardTitle, CardText, CardBody, CardFooter, Button, Container } from "reactstrap";
import { Page, withResolver } from "components/common";
import { formatCurrency } from "services/util";
import { Trans } from "react-i18next";
import { Link } from 'react-router-dom';
import BackButton from "components/refactoring/BackButton";
import { useForm, useOrganisationContext, usePopover } from "hooks";
import LoadingMsg from "components/common/LoadingMsg";
import { getOutlineStyle } from "pages/UserList/Edit";
import { PlansList } from "./PlansList";


const getKlassBasedOnPlan = (currentPlan, plan) => {
    if (!currentPlan) return ''

    if (currentPlan?.subscription_serial === plan?.order) {
        return 'current-plan'
    } else if (currentPlan?.subscription_serial < plan?.order) {
        return 'upgradable-plan'
    } else {
        return 'disabled-plan'
    }
}

export const ChangeSubscription = ({ client, subscriptionPlans, history, t }) => { 

    const { selectedOrganisation } = useOrganisationContext()
    const [loading, setLoading] = useState(true);
    const [subscription, setSubscription] = useState({})
  
    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await client.get(`connected_organisations/${selectedOrganisation.id}/subscription`);
                const data = res.data;
                
                if (data?.current_plan?.subscription_status !== 'Cancelled') {
                    setSubscription(data);
                }
                
                setLoading(false)
            } catch (error) {
                console.error("Error fetching data:", error);
                setLoading(false)
            }
        } 
    
        fetchData();
    }, [selectedOrganisation])

    const goToPaymentSummaryPage = (planId, action = '') => {
        let params = {
            pathname: `/subscription-summary/${planId}`,
        }

        if (action !== '') {
            params['search'] = `action=${action}`
        }

        history.push(params)
    }

    const upPlans = subscription?.current_plan ? subscriptionPlans?.filter(all => all.order >= subscription?.current_plan?.subscription_serial) : subscriptionPlans

    const oneToFour = upPlans?.filter(item => item.order >= 1 && item.order <= 4)

    return (
        <Page>
            <BackButton/>
            <h4><Trans>Upgrade / Change Plan</Trans></h4>
              {loading ? <LoadingMsg/> :
                <>
                  {oneToFour && oneToFour.length < 2 && (
                    <div className="text-center mt-3 d-flex flex-column">
                    {subscription?.current_plan?.subscription_serial === 4 && (
                      <h5 className="mb-2"><Trans>You are on the highest available plan!</Trans></h5>
                    )}
                    {oneToFour.length === 0 && (
                      <>
                        <h5 className="mb-2"><Trans>Sorry! No Plans Available to Upgrade</Trans></h5>
                        <p className="text-muted"><Trans>Your plan is either a limited-time purchase or has been removed.</Trans></p>
                      </>
                    )}
                    </div>
                  )}
                 <Row className="mt-4 all-plans-container justify-content-center">
                    {oneToFour && oneToFour.map((item) => {
                        const languageList = item.subscription_plan_inclusives.filter(i => i.content_type === "hesaablanguage")
                        const moduleList = item.subscription_plan_inclusives.filter(i => i.content_type === "hesaabmodule")
                        const appList = item.subscription_plan_inclusives.filter(i => i.content_type === "hesaabapplication")
                        const subscriptionType = getKlassBasedOnPlan(subscription?.current_plan, item)
                        return (
                            <Col key={item.id} xs="12" md="3" sm="6" lg="3">
                                <Card className={`subscription-plan-card ${subscriptionType}`}>
                                    {(!subscription?.current_plan || item.order >= subscription?.current_plan?.subscription_serial) && <CardBody className="text-center">
                                        <div className="pricing-table-title">
                                            <h4 className="pricing-title"><Trans>{item.name}</Trans> </h4>
                                        </div>
                                        <div className="pricing-table-price">
                                                <p>
                                                <span className="pricing-price">{ item.yearly_price?.price } { item.yearly_price?.currency }</span>
                                                <span className="pricing-period">
                                                    / 
                                                    <Trans>Year</Trans>
                                                </span>
                                                <br/>
                                                <span className="pricing-period">
                                                    <Trans>{ item.yearly_price?.is_vat_included ? 'Including VAT' : 'Excluding VAT' }</Trans>
                                                </span>
                                            </p>
                                        </div>

                                        <PlansList moduleList={moduleList} appList={appList} languageList={languageList} featuresList={item?.feature_list} t={t} />

                                    </CardBody>}
                                    {(!subscription?.current_plan || item.order > subscription?.current_plan?.subscription_serial) && <CardFooter className="bg-white">
                                        <Row>
                                            <Col>
                                                <div className="d-flex justify-content-center">
                                                    {!subscription?.current_plan && <Button color="primary" onClick={() => goToPaymentSummaryPage(item.id)}>
                                                        <Trans>Buy Now</Trans>
                                                    </Button>}

                                                    {subscription?.current_plan && item.order > subscription?.current_plan?.subscription_serial && <Button color="primary" onClick={() => goToPaymentSummaryPage(item.id, 'upgrade')}>
                                                        <Trans>Upgrade</Trans>
                                                    </Button>}
                                                </div>
                                            </Col>
                                        </Row>
                                    </CardFooter>}
                                    {subscriptionType === "current-plan" && 
                                        <CardFooter className="bg-white">
                                            <Row>
                                                <Col>
                                                    <div className="d-flex justify-content-center"><span style={getOutlineStyle('#ec4545', '', "1px")}><Trans>Current Plan</Trans></span></div>
                                                </Col>
                                            </Row>
                                        </CardFooter>
                                    }
                                </Card>
                            </Col>
                        )
                    })
                }
            </Row>
            </>}
        </Page>
    )
}

export const enhance = compose(
  withStateHandlers(
    {},
    {
      setSubscriptionPlans: () => (subscriptionPlans) => ({ subscriptionPlans }),
    }
  ),
  withResolver(
    {
      subscriptionPlans: () => (client) => client.get("subscription-plans"),
    },
    {
      onResolved:
        ({ setSubscriptionPlans }) =>
        ({ subscriptionPlans }) =>
        setSubscriptionPlans(subscriptionPlans),
    }
  )
);

export default enhance(ChangeSubscription);
